import React, { useEffect, useMemo, useRef, useState } from "react";
import { baseUrl } from "../../../../constants/api";
import { API_ENDPOINTS } from "../../../../constants/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { message } from "antd";

const A4_DIMENSIONS = {
  landscape: { width: 842, height: 595 },
  portrait: { width: 595, height: 842 },
};

type TemplateField = {
  key: string;
  type: "variable" | "text";
  label: string;
  text?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  align: "left" | "center" | "right";
  color: string;
};

type TemplateRecord = {
  _id: string;
  name: string;
  orientation: "landscape" | "portrait";
  background_url: string;
  fields: Array<{
    key: string;
    type?: "variable" | "text";
    label?: string;
    text?: string;
    x: number;
    y: number;
    width?: number;
    height?: number;
    fontSize: number;
    align: "left" | "center" | "right";
    color?: string;
  }>;
};

const VARIABLE_DEFINITIONS = [
  { key: "quiz_name", label: "Quiz Name", x: 50, y: 24, width: 24, height: 8, fontSize: 14, align: "center" as const, color: "#1b256b" },
  { key: "certificate_id", label: "Certificate Number", x: 84.65, y: 11.55, width: 18, height: 8, fontSize: 10, align: "center" as const, color: "#1b256b" },
  { key: "candidate_name", label: "Candidate Name", x: 50, y: 40, width: 28, height: 8, fontSize: 18, align: "center" as const, color: "#1b256b" },
  { key: "designation", label: "Designation", x: 50, y: 47, width: 24, height: 8, fontSize: 12, align: "center" as const, color: "#1b256b" },
  { key: "attended_on", label: "Date", x: 33.64, y: 82.37, width: 18, height: 8, fontSize: 12, align: "center" as const, color: "#1b256b" },
  { key: "attended_on_signature", label: "Footer Date", x: 33.64, y: 82.37, width: 18, height: 8, fontSize: 12, align: "center" as const, color: "#1b256b" },
  { key: "cli_name", label: "CLI Name", x: 66.85, y: 82.37, width: 24, height: 8, fontSize: 12, align: "center" as const, color: "#1b256b" },
];

const DEFAULT_TEXT_FIELDS: TemplateField[] = [
  {
    key: "text_statement_default",
    type: "text",
    label: "Certification Statement",
    text: "I have examined [candidate_name], [designation] on [attended_on] and found him competent to work in the Automatic Signaling Section",
    x: 50,
    y: 57,
    width: 62,
    height: 16,
    fontSize: 12,
    align: "center",
    color: "#1b256b"
  },
];

const SAMPLE_VALUES: Record<string, string> = {
  quiz_name: "Automatic Signaling Quiz",
  candidate_name: "KULDEEP SHARMA",
  designation: "Senior Technician",
  attended_on: "25/03/2026",
  attended_on_signature: "25/03/2026",
  cli_name: "SOUBHAGYA KUMAR SWAIN",
  certificate_id: "CERT-20260325-1A2B3C",
};

const PLACEHOLDER_HINTS = ["candidate_name", "designation", "attended_on", "cli_name", "certificate_id", "quiz_name", "blank", "blank:20"];

const renderPreviewText = (field: TemplateField) => {
  const baseText = field.type === "text" ? (field.text || field.label || "Custom Text") : (SAMPLE_VALUES[field.key] || field.label);
  return baseText.replace(/\[([^\]]+)\]/g, (_, rawToken) => {
    const token = String(rawToken || "").trim();
    if (!token) return "";

    const blankMatch = token.match(/^blank(?::(\d+))?$/i);
    if (blankMatch) {
      const length = Math.max(6, Math.min(40, Number(blankMatch[1]) || 14));
      return "_".repeat(length);
    }

    return SAMPLE_VALUES[token] || `[${token}]`;
  });
};

const getPreviewFontSize = (field: TemplateField, previewScale: number) => {
  const px = field.fontSize * previewScale * (96 / 72);
  if (field.type === "text") {
    return Math.max(8, px);
  }

  return Math.max(8, px);
};

const getPreviewMinWidth = (field: TemplateField) => {
  if (field.key === "candidate_name") return "34%";
  if (field.key === "quiz_name") return "26%";
  if (field.type === "text" && field.text && field.text.length > 35) return "42%";
  return "fit-content";
};

type Props = {
  token: string;
  onCreated: (template: TemplateRecord) => void;
  onCancel: () => void;
};

const CertificateTemplateDesigner = ({ token, onCreated, onCancel }: Props) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const dragFieldRef = useRef<string | null>(null);
  const resizeFieldRef = useRef<string | null>(null);
  const [name, setName] = useState("");
  const [orientation, setOrientation] = useState<"landscape" | "portrait">("landscape");
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [backgroundPreview, setBackgroundPreview] = useState("");
  const [fields, setFields] = useState<TemplateField[]>(DEFAULT_TEXT_FIELDS);
  const [selectedFieldKey, setSelectedFieldKey] = useState<string>(DEFAULT_TEXT_FIELDS[0]?.key || "");
  const [saving, setSaving] = useState(false);
  const [testingPdf, setTestingPdf] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);

  useEffect(() => {
    return () => {
      if (backgroundPreview.startsWith("blob:")) {
        URL.revokeObjectURL(backgroundPreview);
      }
    };
  }, [backgroundPreview]);

  useEffect(() => {
    if (!previewRef.current) {
      return;
    }

    const dimensions = A4_DIMENSIONS[orientation];
    const updateScale = () => {
      if (!previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      setPreviewScale(rect.width / dimensions.width);
    };

    updateScale();

    const observer = new ResizeObserver(() => updateScale());
    observer.observe(previewRef.current);

    return () => observer.disconnect();
  }, [orientation]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const activeKey = dragFieldRef.current;
      const resizingKey = resizeFieldRef.current;
      if ((!activeKey && !resizingKey) || !previewRef.current) return;

      const rect = previewRef.current.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      if (resizingKey) {
        setFields((prev) =>
          prev.map((field) => {
            if (field.key !== resizingKey) return field;

            const nextWidth = Math.max(8, Math.min(90, Math.abs(x - field.x) * 2));
            const nextHeight = Math.max(6, Math.min(60, Math.abs(y - field.y) * 2));
            return {
              ...field,
              width: Number(nextWidth.toFixed(2)),
              height: Number(nextHeight.toFixed(2)),
            };
          })
        );
        return;
      }

      setFields((prev) =>
        prev.map((field) =>
          field.key === activeKey
            ? {
                ...field,
                x: Math.max(0, Math.min(100, Number(x.toFixed(2)))),
                y: Math.max(0, Math.min(100, Number(y.toFixed(2)))),
              }
            : field
        )
      );
    };

    const handleMouseUp = () => {
      dragFieldRef.current = null;
      resizeFieldRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const selectedField = useMemo(
    () => fields.find((field) => field.key === selectedFieldKey) || fields[0],
    [fields, selectedFieldKey]
  );

  const updateField = (key: string, patch: Partial<TemplateField>) => {
    setFields((prev) => prev.map((field) => (field.key === key ? { ...field, ...patch } : field)));
  };

  const addOptionalField = (key: string) => {
    const fieldDefinition = VARIABLE_DEFINITIONS.find((field) => field.key === key);
    if (!fieldDefinition) return;

    setFields((prev) => {
      if (prev.some((field) => field.key === key)) {
        return prev;
      }
      return [...prev, { ...fieldDefinition, type: "variable", text: "" }];
    });
    setSelectedFieldKey(key);
  };

  const addTextField = () => {
    const nextNumber = fields.filter((field) => field.type === "text").length + 1;
    const key = `text_${Date.now()}`;
    const newField: TemplateField = {
      key,
      type: "text",
      label: `Custom Text ${nextNumber}`,
      text: "Custom Text",
      x: 50,
      y: 50,
      width: 36,
      height: 12,
      fontSize: 12,
      align: "center",
      color: "#1b256b",
    };
    setFields((prev) => [...prev, newField]);
    setSelectedFieldKey(key);
  };

  const removeField = (key: string) => {
    setFields((prev) => prev.filter((field) => field.key !== key));
    if (selectedFieldKey === key) {
      setSelectedFieldKey(fields.find((field) => field.key !== key)?.key || "");
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setBackgroundFile(file);
    if (backgroundPreview.startsWith("blob:")) {
      URL.revokeObjectURL(backgroundPreview);
    }
    setBackgroundPreview(file ? URL.createObjectURL(file) : "");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      message.error("Template name is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("orientation", orientation);
    formData.append(
      "fields",
      JSON.stringify(fields.map(({ key, type, label, text, x, y, width, height, fontSize, align, color }) => ({ key, type, label, text, x, y, width, height, fontSize, align, color })))
    );
    if (backgroundFile) {
      formData.append("file", backgroundFile);
    }

    setSaving(true);
    try {
      const response = await fetch(`${baseUrl}${API_ENDPOINTS.CREATE_CERTIFICATE_TEMPLATE}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Failed to save certificate template");
      }

      message.success("Certificate template created");
      onCreated(data.template);
    } catch (error: any) {
      message.error(error.message || "Failed to save certificate template");
    } finally {
      setSaving(false);
    }
  };

  const handleTestPdf = async () => {
    const formData = new FormData();
    formData.append("name", name.trim() || "Certificate Test");
    formData.append("orientation", orientation);
    formData.append(
      "fields",
      JSON.stringify(fields.map(({ key, type, label, text, x, y, width, height, fontSize, align, color }) => ({ key, type, label, text, x, y, width, height, fontSize, align, color })))
    );
    if (backgroundFile) {
      formData.append("file", backgroundFile);
    }

    setTestingPdf(true);
    try {
      const response = await fetch(`${baseUrl}${API_ENDPOINTS.PREVIEW_CERTIFICATE_TEMPLATE}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || "Failed to generate test PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(name.trim() || "certificate_test").replace(/[^a-z0-9]+/gi, "_").toLowerCase()}_preview.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(error.message || "Failed to generate test PDF");
    } finally {
      setTestingPdf(false);
    }
  };

  const previewStyle = orientation === "landscape"
    ? { aspectRatio: "1.414 / 1", maxWidth: "720px" }
    : { aspectRatio: "1 / 1.414", maxWidth: "420px" };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-4">
          <div className="cq-field">
            <label className="cq-label">Template Name <span>*</span></label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="A4 Locomotive Certificate"
              className="h-12 rounded-xl border-[1.5px] border-[#e0e0e0] bg-[#f8f9ff]"
            />
          </div>

          <div className="cq-field">
            <label className="cq-label">Orientation <span>*</span></label>
            <select
              className="cq-select"
              value={orientation}
              onChange={(e) => setOrientation(e.target.value as "landscape" | "portrait")}
            >
              <option value="landscape">A4 Landscape</option>
              <option value="portrait">A4 Portrait</option>
            </select>
          </div>

          <div className="cq-field">
            <label className="cq-label">Background Template</label>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileChange}
              className="h-12 rounded-xl border-[1.5px] border-[#e0e0e0] bg-[#f8f9ff] pt-3"
            />
            <p style={{ fontSize: "11px", color: "#888", margin: "4px 0 0 0" }}>
              Upload a full A4 background image. PNG is recommended.
            </p>
          </div>

          <div className="cq-field">
            <label className="cq-label">Field Controls</label>
            <select
              className="cq-select"
              value={selectedFieldKey}
              onChange={(e) => setSelectedFieldKey(e.target.value)}
            >
              {fields.map((field) => (
                <option key={field.key} value={field.key}>
                  {field.label || field.key}
                </option>
              ))}
            </select>
          </div>

          <div className="cq-field">
            <label className="cq-label">Add Variables</label>
            <div className="flex flex-wrap gap-2">
              {VARIABLE_DEFINITIONS.filter((field) => !fields.some((activeField) => activeField.key === field.key)).map((field) => (
                <Button
                  key={field.key}
                  type="button"
                  variant="outline"
                  onClick={() => addOptionalField(field.key)}
                  className="h-9 rounded-full text-xs"
                >
                  Add {field.label}
                </Button>
              ))}
              {VARIABLE_DEFINITIONS.filter((field) => !fields.some((activeField) => activeField.key === field.key)).length === 0 && (
                <span className="text-xs text-[#777]">All variables already added.</span>
              )}
            </div>
            <div className="mt-3">
              <Button type="button" variant="outline" onClick={addTextField} className="h-9 rounded-full text-xs">
                Add Text Block
              </Button>
            </div>
            <p style={{ fontSize: "11px", color: "#777", margin: "10px 0 0 0" }}>
              Use placeholders like {PLACEHOLDER_HINTS.map((hint) => `[${hint}]`).join(", ")} inside a text block.
            </p>
          </div>

          {selectedField && (
            <div className="grid grid-cols-2 gap-3 rounded-2xl border border-[#ececec] bg-[#fafafa] p-4">
              {selectedField.type === "text" && (
                <div className="cq-field col-span-2">
                  <label className="cq-label">Text Content</label>
                  <textarea
                    value={selectedField.text || ""}
                    onChange={(e) => updateField(selectedField.key, { text: e.target.value })}
                    className="min-h-[110px] w-full rounded-xl border-[1.5px] border-[#e0e0e0] bg-white p-3 text-sm outline-none"
                  />
                </div>
              )}
              <div className="cq-field">
                <label className="cq-label">Font Size</label>
                <Input
                  type="number"
                  min={8}
                  max={48}
                  value={selectedField.fontSize}
                  onChange={(e) => updateField(selectedField.key, { fontSize: Math.max(8, Math.min(48, Number(e.target.value) || 16)) })}
                  className="h-11 rounded-xl border-[1.5px] border-[#e0e0e0] bg-white"
                />
              </div>
              <div className="cq-field">
                <label className="cq-label">Align</label>
                <select
                  className="cq-select"
                  value={selectedField.align}
                  onChange={(e) => updateField(selectedField.key, { align: e.target.value as "left" | "center" | "right" })}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
              <div className="cq-field">
                <label className="cq-label">Color</label>
                <Input
                  type="color"
                  value={selectedField.color}
                  onChange={(e) => updateField(selectedField.key, { color: e.target.value })}
                  className="h-11 rounded-xl border-[1.5px] border-[#e0e0e0] bg-white p-1"
                />
              </div>
              <div className="cq-field">
                <label className="cq-label">Position</label>
                <div className="text-sm text-[#555] pt-3">
                  X: {selectedField.x.toFixed(1)}%, Y: {selectedField.y.toFixed(1)}%
                </div>
              </div>
              {selectedField.type === "text" && (
                <>
                  <div className="cq-field">
                    <label className="cq-label">Box Width</label>
                    <Input
                      type="number"
                      min={8}
                      max={90}
                      value={selectedField.width}
                      onChange={(e) => updateField(selectedField.key, { width: Math.max(8, Math.min(90, Number(e.target.value) || 36)) })}
                      className="h-11 rounded-xl border-[1.5px] border-[#e0e0e0] bg-white"
                    />
                  </div>
                  <div className="cq-field">
                    <label className="cq-label">Box Height</label>
                    <Input
                      type="number"
                      min={6}
                      max={60}
                      value={selectedField.height}
                      onChange={(e) => updateField(selectedField.key, { height: Math.max(6, Math.min(60, Number(e.target.value) || 10)) })}
                      className="h-11 rounded-xl border-[1.5px] border-[#e0e0e0] bg-white"
                    />
                  </div>
                </>
              )}
              <div className="col-span-2 flex justify-end">
                <Button type="button" variant="outline" onClick={() => removeField(selectedField.key)}>
                  Remove {selectedField.type === "text" ? "Text Block" : "Variable"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="text-sm font-medium text-[#444]">Layout Preview</div>
          <div
            ref={previewRef}
            className="relative w-full overflow-hidden rounded-2xl border border-[#dcdcdc] bg-[#f6f1e8] shadow-sm"
            style={previewStyle}
          >
            {backgroundPreview ? (
              <img
                src={backgroundPreview}
                alt="Certificate background preview"
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-[linear-gradient(135deg,#f6f1e8_0%,#fffdf8_45%,#ece5d5_100%)]" />
            )}

            {fields.map((field) => (
              <button
                key={field.key}
                type="button"
                onMouseDown={() => {
                  dragFieldRef.current = field.key;
                  setSelectedFieldKey(field.key);
                }}
                onClick={() => setSelectedFieldKey(field.key)}
                className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-md border px-2 py-1 text-center shadow-sm ${selectedFieldKey === field.key ? "border-[#111] bg-[#fff7d6]" : "border-[#bbb] bg-white/90"}`}
                style={{
                  left: `${field.x}%`,
                  top: `${field.y}%`,
                  fontSize: `${getPreviewFontSize(field, previewScale)}px`,
                  color: field.color,
                  minWidth: getPreviewMinWidth(field),
                  width: field.type === "text" ? `${field.width}%` : undefined,
                  minHeight: field.type === "text" ? `${field.height}%` : undefined,
                  maxWidth: field.type === "text" ? "none" : field.key === "candidate_name" ? "38%" : "22%",
                  padding: field.key === "candidate_name"
                    ? `${Math.max(4, 6 * previewScale)}px ${Math.max(8, 12 * previewScale)}px`
                    : `${Math.max(3, 4 * previewScale)}px ${Math.max(6, 8 * previewScale)}px`,
                  lineHeight: 1.15,
                  whiteSpace: field.type === "text" ? "pre-wrap" : "nowrap",
                  overflow: field.type === "text" ? "hidden" : undefined,
                  cursor: "move",
                }}
              >
                {renderPreviewText(field)}
                {field.type === "text" && (
                  <span
                    onMouseDown={(event) => {
                      event.stopPropagation();
                      resizeFieldRef.current = field.key;
                      setSelectedFieldKey(field.key);
                    }}
                    style={{
                      position: "absolute",
                      right: 4,
                      bottom: 4,
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: "#253b6e",
                      cursor: "nwse-resize",
                    }}
                  />
                )}
              </button>
            ))}
          </div>
          <p style={{ fontSize: "11px", color: "#888", margin: 0 }}>
            Drag fields on the preview to place them. Positions are saved relative to the A4 page size.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="button" variant="outline" onClick={handleTestPdf} disabled={testingPdf}>
          {testingPdf ? "Generating PDF..." : "Test PDF"}
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Template"}
        </Button>
      </div>
    </div>
  );
};

export default CertificateTemplateDesigner;
